// Authentication helper utilities
import { jwtDecode } from 'jwt-decode';

/**
 * Get token from cookies
 * @returns {string|null} Token or null if not found
 */
export const getTokenFromCookie = () => {
  const name = 'token=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  
  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return null;
};

/**
 * Check if user is authenticated by checking for token cookie
 * @deprecated Cookie is now httpOnly - use server verification instead
 * @returns {boolean} Always returns false (httpOnly cookies can't be read by JS)
 */
export const isAuthenticated = () => {
  // ⚠️ Cookie is httpOnly - JavaScript cannot read it
  // Must verify authentication via server API call
  return false;
};

/**
 * Remove token cookie (logout)
 * @deprecated Cookie is now httpOnly - server must clear it
 * This is a no-op since httpOnly cookies can't be removed by JavaScript
 */
export const removeToken = () => {
  // ⚠️ Cookie is httpOnly - JavaScript cannot delete it
  // Server must clear cookie via /auth/logout endpoint
  console.log('⚠️ [AUTH] removeToken called but cookie is httpOnly - no action taken');
};

/**
 * Decode JWT token (simple base64 decode, no verification)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Get user data from token
 * @param {string} token - JWT token
 * @returns {object|null} User data or null
 */
export const getUserFromToken = (token) => {
  if (!token) return null;
  
  const decoded = decodeToken(token);
  if (!decoded) return null;
  
  return {
    id: decoded.id,
    name: decoded.name,
    email: decoded.email,
    full_name: decoded.full_name,
    exp: decoded.exp
  };
};

/**
 * Check if user is authenticated and token is valid
 * @deprecated Cookie is now httpOnly - use server verification instead
 * @returns {boolean} Always returns false (httpOnly cookies can't be read by JS)
 */
export const isValidAuth = () => {
  // ⚠️ Cookie is httpOnly - JavaScript cannot read it
  // Must verify authentication via server API call
  console.log('🔍 [AUTH] isValidAuth: Cookie is httpOnly - cannot validate client-side');
  return false;
};

/**
 * Decode JWT token using jwt-decode library
 * @param {string} token - JWT token to decode
 * @returns {object|null} Decoded payload with user ID or null if invalid
 */
export const decodeTokenWithLibrary = (token) => {
  if (!token) {
    console.error('No token provided');
    return null;
  }

  try {
    // Decode the token (no verification, just decoding)
    const decoded = jwtDecode(token);
    return {
      id: decoded.id,
      name: decoded.name,
      email: decoded.email,
      full_name: decoded.full_name,
      exp: decoded.exp,
      iat: decoded.iat
    };
  } catch (error) {
    console.error('Token decoding failed:', error.message);
    return null;
  }
};

/**
 * Get user ID from current JWT token
 * @returns {string|null} User ID or null if token is invalid
 */
export const getUserIdFromToken = () => {
  const token = getTokenFromCookie();
  if (!token) {
    console.log('No token found in cookies');
    return null;
  }

  const decoded = decodeTokenWithLibrary(token);
  if (!decoded) {
    console.log('Failed to decode token');
    return null;
  }

  return decoded.id;
};